/**
 * API Configuration
 * 
 * This file contains all API-related configuration settings.
 * For local development, create a .env.local file with:
 * 
 * VITE_API_BASE_URL=https://test.filesdna.com
 * VITE_DEFAULT_LANG=ar_001
 * VITE_API_DEBUG=true
 */

export const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'https://mos.filesdna.com',
  defaultLang: import.meta.env.VITE_DEFAULT_LANG || 'ar_001',
  debug: import.meta.env.VITE_API_DEBUG === 'true',
  timeout: 30000, // 30 seconds
};

/**
 * Language mapping between frontend and Odoo
 * Frontend uses: ar, en, ku
 * Odoo expects: ar_001, en_US, ku_IQ
 */
export const LANGUAGE_MAP = {
  ar: 'ar_001',  // Arabic -> ar_001
  en: 'en_US',   // English -> en_US
  ku: 'ku_IQ',   // Kurdish -> ku_IQ
} as const;

export type FrontendLanguage = keyof typeof LANGUAGE_MAP;
export type OdooLanguage = (typeof LANGUAGE_MAP)[FrontendLanguage];
type SupportedLanguage = FrontendLanguage | OdooLanguage;

/**
 * Get Odoo language code from frontend language
 * Ensures we always pass the correct Odoo locale code (e.g., en_US, ar_001, ku_IQ)
 * 
 * Mapping:
 * - ar or ar_001 -> ar_001 (Arabic)
 * - en or en_US -> en_US (English US)
 * - ku or ku_IQ -> ku_IQ (Kurdish)
 */
export function getOdooLang(frontendLang: SupportedLanguage): OdooLanguage {
  // If already a full locale code, validate and return
  if (frontendLang.includes('_')) {
    if (
      frontendLang === LANGUAGE_MAP.ar ||
      frontendLang === LANGUAGE_MAP.en ||
      frontendLang === LANGUAGE_MAP.ku
    ) {
      return frontendLang;
    }
    // If invalid full code, try to extract the language part
    const langPart = frontendLang.split('_')[0] as FrontendLanguage;
    return LANGUAGE_MAP[langPart] || LANGUAGE_MAP.ar;
  }
  // Map frontend short code to Odoo locale code
  return LANGUAGE_MAP[frontendLang as FrontendLanguage] || LANGUAGE_MAP.ar;
}

/**
 * Build image URL from Odoo path
 * If path is already a full URL (http:// or https://), return it as-is
 * Otherwise, prepend the base URL
 */
export function buildImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  // Check if it's already a full URL
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If path already includes the base URL, return as-is
  if (path.includes(API_CONFIG.baseUrl)) {
    return path;
  }
  // Prepend base URL for relative paths
  return `${API_CONFIG.baseUrl}${path}`;
}

/**
 * Log API call in development
 */
export function logApiCall(method: string, url: string, data?: unknown): void {
  if (API_CONFIG.debug) {
    console.log(`[API] ${method} ${url}`, data);
  }
}

/**
 * Log API error
 */
export function logApiError(error: unknown): void {
  if (API_CONFIG.debug) {
    console.error('[API Error]', error);
  }
}
